import React, { useState, useEffect, useRef, useCallback } from 'react';
import { theme } from '../../styles/theme';

interface JigsawPlayerProps {
    imageUrl: string;
    rows?: number;
    cols?: number;
    onComplete?: () => void;
}

interface PieceEdges {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

interface Piece {
    id: number;
    row: number;
    col: number;
    x: number;
    y: number;
    correctX: number;
    correctY: number;
    isLocked: boolean;
    edges: PieceEdges;
}

const generateEdgePattern = (rows: number, cols: number): PieceEdges[][] => {
    const pattern: PieceEdges[][] = [];
    for (let r = 0; r < rows; r++) {
        pattern[r] = [];
        for (let c = 0; c < cols; c++) {
            pattern[r][c] = {
                top: r === 0 ? 0 : -pattern[r - 1][c].bottom,
                right: c === cols - 1 ? 0 : (Math.random() > 0.5 ? 1 : -1),
                bottom: r === rows - 1 ? 0 : (Math.random() > 0.5 ? 1 : -1),
                left: c === 0 ? 0 : -pattern[r][c - 1].right,
            };
        }
    }
    return pattern;
};

const drawPiecePath = (
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    width: number, height: number,
    edges: PieceEdges,
    tabSize: number
) => {
    const tw = tabSize;
    const th = tabSize * 0.8;

    ctx.beginPath();
    ctx.moveTo(x, y);

    // Top
    if (edges.top === 0) {
        ctx.lineTo(x + width, y);
    } else {
        const mx = x + width / 2;
        ctx.lineTo(mx - tw / 2, y);
        const dir = edges.top === 1 ? -1 : 1;
        ctx.bezierCurveTo(mx - tw / 2, y + dir * th * 0.5, mx - tw * 0.8, y + dir * th, mx, y + dir * th);
        ctx.bezierCurveTo(mx + tw * 0.8, y + dir * th, mx + tw / 2, y + dir * th * 0.5, mx + tw / 2, y);
        ctx.lineTo(x + width, y);
    }

    // Right
    if (edges.right === 0) {
        ctx.lineTo(x + width, y + height);
    } else {
        const my = y + height / 2;
        ctx.lineTo(x + width, my - tw / 2);
        const dir = edges.right === 1 ? 1 : -1;
        ctx.bezierCurveTo(x + width + dir * th * 0.5, my - tw / 2, x + width + dir * th, my - tw * 0.8, x + width + dir * th, my);
        ctx.bezierCurveTo(x + width + dir * th, my + tw * 0.8, x + width + dir * th * 0.5, my + tw / 2, x + width, my + tw / 2);
        ctx.lineTo(x + width, y + height);
    }

    // Bottom
    if (edges.bottom === 0) {
        ctx.lineTo(x, y + height);
    } else {
        const mx = x + width / 2;
        ctx.lineTo(mx + tw / 2, y + height);
        const dir = edges.bottom === 1 ? 1 : -1;
        ctx.bezierCurveTo(mx + tw / 2, y + height + dir * th * 0.5, mx + tw * 0.8, y + height + dir * th, mx, y + height + dir * th);
        ctx.bezierCurveTo(mx - tw * 0.8, y + height + dir * th, mx - tw / 2, y + height + dir * th * 0.5, mx - tw / 2, y + height);
        ctx.lineTo(x, y + height);
    }

    // Left
    if (edges.left === 0) {
        ctx.lineTo(x, y);
    } else {
        const my = y + height / 2;
        ctx.lineTo(x, my + tw / 2);
        const dir = edges.left === 1 ? -1 : 1;
        ctx.bezierCurveTo(x + dir * th * 0.5, my + tw / 2, x + dir * th, my + tw * 0.8, x + dir * th, my);
        ctx.bezierCurveTo(x + dir * th, my - tw * 0.8, x + dir * th * 0.5, my - tw / 2, x, my - tw / 2);
        ctx.lineTo(x, y);
    }

    ctx.closePath();
};

const JigsawPlayer: React.FC<JigsawPlayerProps> = ({ imageUrl, rows = 4, cols = 4, onComplete }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [canvasSize, setCanvasSize] = useState({ w: 350, h: 450 });
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [pieces, setPieces] = useState<Piece[]>([]);
    const [edgePattern, setEdgePattern] = useState<PieceEdges[][] | null>(null);
    const [draggingId, setDraggingId] = useState<number | null>(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [completed, setCompleted] = useState(false);
    const [timer, setTimer] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [showGuide, setShowGuide] = useState(true);
    const [puzzleLayout, setPuzzleLayout] = useState({ x: 0, y: 0, w: 0, h: 0, pieceW: 0, pieceH: 0, tabSize: 0 });

    // Set canvas size based on window - mobile first
    useEffect(() => {
        const updateSize = () => {
            const w = Math.min(window.innerWidth - 24, 900);
            const h = Math.min(window.innerHeight - 200, 700);
            setCanvasSize({ w, h });
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Load image
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setImage(img);
        img.onerror = () => console.error('Failed to load image');
        img.src = imageUrl;
    }, [imageUrl]);

    // Initialize pieces when image loads or canvas resizes
    useEffect(() => {
        if (!image || canvasSize.w === 0) return;

        const pattern = generateEdgePattern(rows, cols);
        setEdgePattern(pattern);

        // Puzzle takes 70% of canvas, centered
        const maxPuzzleW = canvasSize.w * 0.70;
        const maxPuzzleH = canvasSize.h * 0.70;
        const imgAspect = image.width / image.height;

        let puzzleW: number, puzzleH: number;
        if (maxPuzzleW / imgAspect <= maxPuzzleH) {
            puzzleW = maxPuzzleW;
            puzzleH = puzzleW / imgAspect;
        } else {
            puzzleH = maxPuzzleH;
            puzzleW = puzzleH * imgAspect;
        }

        const pieceW = puzzleW / cols;
        const pieceH = puzzleH / rows;
        const puzzleX = (canvasSize.w - puzzleW) / 2;
        const puzzleY = (canvasSize.h - puzzleH) / 2;
        const tabSize = Math.min(pieceW, pieceH) * 0.28;

        setPuzzleLayout({ x: puzzleX, y: puzzleY, w: puzzleW, h: puzzleH, pieceW, pieceH, tabSize });

        // Scatter pieces randomly
        const newPieces: Piece[] = [];
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const px = 15 + Math.random() * (canvasSize.w - pieceW - 30);
                const py = 15 + Math.random() * (canvasSize.h - pieceH - 30);

                newPieces.push({
                    id: r * cols + c,
                    row: r,
                    col: c,
                    x: px,
                    y: py,
                    correctX: puzzleX + c * pieceW,
                    correctY: puzzleY + r * pieceH,
                    isLocked: false,
                    edges: pattern[r][c],
                });
            }
        }
        setPieces(newPieces.sort(() => Math.random() - 0.5));
    }, [image, rows, cols, canvasSize]);

    // Timer
    useEffect(() => {
        if (isPlaying && !completed) {
            const interval = setInterval(() => setTimer(t => t + 1), 1000);
            return () => clearInterval(interval);
        }
    }, [isPlaying, completed]);

    // Check completion
    useEffect(() => {
        if (pieces.length > 0 && pieces.every(p => p.isLocked)) {
            setCompleted(true);
            setIsPlaying(false);
            // Call the completion callback to update user profile
            if (onComplete) {
                onComplete();
            }
        }
    }, [pieces, onComplete]);

    // Draw everything
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !image || !edgePattern || pieces.length === 0) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { x: puzzleX, y: puzzleY, w: puzzleW, h: puzzleH, pieceW, pieceH, tabSize } = puzzleLayout;
        if (pieceW === 0) return;

        // Clear canvas
        ctx.fillStyle = '#E5DDD0';
        ctx.fillRect(0, 0, canvasSize.w, canvasSize.h);

        // Draw guide area
        if (showGuide) {
            ctx.fillStyle = 'rgba(100, 80, 60, 0.1)';
            ctx.fillRect(puzzleX, puzzleY, puzzleW, puzzleH);
            ctx.strokeStyle = 'rgba(100, 80, 60, 0.25)';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.strokeRect(puzzleX, puzzleY, puzzleW, puzzleH);
            ctx.setLineDash([]);
        }

        // Sort pieces: locked first, dragging last
        const sorted = [...pieces].sort((a, b) => {
            if (a.isLocked !== b.isLocked) return a.isLocked ? -1 : 1;
            if (a.id === draggingId) return 1;
            if (b.id === draggingId) return -1;
            return 0;
        });

        const srcPieceW = image.width / cols;
        const srcPieceH = image.height / rows;
        const srcTabSize = Math.min(srcPieceW, srcPieceH) * 0.22;

        sorted.forEach(piece => {
            ctx.save();

            // Draw piece shape
            drawPiecePath(ctx, piece.x, piece.y, pieceW, pieceH, piece.edges, tabSize);

            // Shadow
            if (!piece.isLocked) {
                ctx.shadowColor = 'rgba(0,0,0,0.35)';
                ctx.shadowBlur = 6;
                ctx.shadowOffsetX = 2;
                ctx.shadowOffsetY = 2;
            }

            ctx.fillStyle = '#fff';
            ctx.fill();
            ctx.clip();

            // Draw image portion
            const srcX = piece.col * srcPieceW - (piece.edges.left !== 0 ? srcTabSize : 0);
            const srcY = piece.row * srcPieceH - (piece.edges.top !== 0 ? srcTabSize : 0);
            const srcW = srcPieceW + (piece.edges.left !== 0 ? srcTabSize : 0) + (piece.edges.right !== 0 ? srcTabSize : 0);
            const srcH = srcPieceH + (piece.edges.top !== 0 ? srcTabSize : 0) + (piece.edges.bottom !== 0 ? srcTabSize : 0);

            const destX = piece.x - (piece.edges.left !== 0 ? tabSize : 0);
            const destY = piece.y - (piece.edges.top !== 0 ? tabSize : 0);
            const destW = pieceW + (piece.edges.left !== 0 ? tabSize : 0) + (piece.edges.right !== 0 ? tabSize : 0);
            const destH = pieceH + (piece.edges.top !== 0 ? tabSize : 0) + (piece.edges.bottom !== 0 ? tabSize : 0);

            ctx.drawImage(image, Math.max(0, srcX), Math.max(0, srcY), srcW, srcH, destX, destY, destW, destH);

            ctx.restore();

            // Outline
            ctx.save();
            drawPiecePath(ctx, piece.x, piece.y, pieceW, pieceH, piece.edges, tabSize);
            ctx.strokeStyle = piece.isLocked ? '#4a7c4a' : 'rgba(60,50,40,0.25)';
            ctx.lineWidth = piece.isLocked ? 2.5 : 1;
            ctx.stroke();
            ctx.restore();
        });
    }, [image, pieces, edgePattern, draggingId, showGuide, canvasSize, puzzleLayout, cols, rows]);

    useEffect(() => {
        draw();
    }, [draw]);

    // Get position from mouse/touch event
    const getPos = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
        const clientY = 'touches' in e ? e.touches[0]?.clientY ?? 0 : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    // Find piece at position
    const findPieceAt = (x: number, y: number): Piece | null => {
        const { pieceW, pieceH, tabSize } = puzzleLayout;
        const margin = tabSize + 5;

        for (let i = pieces.length - 1; i >= 0; i--) {
            const p = pieces[i];
            if (p.isLocked) continue;
            if (x >= p.x - margin && x <= p.x + pieceW + margin &&
                y >= p.y - margin && y <= p.y + pieceH + margin) {
                return p;
            }
        }
        return null;
    };

    const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        if (draggingId !== null) return;
        const pos = getPos(e);
        const piece = findPieceAt(pos.x, pos.y);
        if (piece) {
            setDraggingId(piece.id);
            setDragOffset({ x: pos.x - piece.x, y: pos.y - piece.y });
            // Move to top of render order
            setPieces(prev => {
                const idx = prev.findIndex(p => p.id === piece.id);
                if (idx === -1) return prev;
                const arr = [...prev];
                const [removed] = arr.splice(idx, 1);
                arr.push(removed);
                return arr;
            });
        }
    };

    const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (draggingId === null) return;
        e.preventDefault();
        const pos = getPos(e);
        const newX = pos.x - dragOffset.x;
        const newY = pos.y - dragOffset.y;

        setPieces(prev => prev.map(p =>
            p.id === draggingId ? { ...p, x: newX, y: newY } : p
        ));
    };

    const handleEnd = () => {
        if (draggingId === null) return;

        const { pieceW, pieceH } = puzzleLayout;
        const tolerance = Math.min(pieceW, pieceH) * 0.4;

        setPieces(prev => prev.map(p => {
            if (p.id !== draggingId) return p;
            const dist = Math.sqrt((p.x - p.correctX) ** 2 + (p.y - p.correctY) ** 2);
            if (dist < tolerance) {
                return { ...p, x: p.correctX, y: p.correctY, isLocked: true };
            }
            return p;
        }));

        setDraggingId(null);
    };

    const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    const resetPuzzle = () => {
        setCompleted(false);
        setTimer(0);
        setIsPlaying(true);
        setEdgePattern(null);
        setPieces([]);
        setImage(null);
        setTimeout(() => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => setImage(img);
            img.src = imageUrl;
        }, 50);
    };

    if (!image) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧩</div>
                <div style={{ color: theme.colors.textSecondary }}>Loading puzzle...</div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {/* Stats bar */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: 'auto',
                marginBottom: '12px',
                background: theme.colors.cardBg,
                padding: '10px 14px',
                borderRadius: '10px',
                fontSize: '14px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                gap: '12px',
                flexWrap: 'wrap'
            }}>
                <span>⏱️ <strong>{formatTime(timer)}</strong></span>
                <span>🧩 <strong>{pieces.filter(p => p.isLocked).length}/{pieces.length}</strong></span>
                <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                    <button
                        onClick={() => setShowGuide(s => !s)}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            background: showGuide ? theme.colors.secondary : '#ddd',
                            color: showGuide ? '#fff' : '#333',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Guide
                    </button>
                    <button
                        onClick={resetPuzzle}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            background: theme.colors.accent,
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Reset
                    </button>
                </div>
            </div>

            {/* Canvas */}
            <canvas
                ref={canvasRef}
                width={canvasSize.w}
                height={canvasSize.h}
                style={{
                    borderRadius: '12px',
                    cursor: draggingId !== null ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                    display: 'block',
                    marginBottom: '14px'
                }}
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
            />

            {/* Tip */}
            <div style={{ fontSize: '13px', color: theme.colors.textSecondary, textAlign: 'center', padding: '0 16px', marginBottom: '8px' }}>
                💡 Drag pieces to the dotted area. They'll snap & turn green when correct!
            </div>

            {/* Completion modal */}
            {completed && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.85)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        background: theme.colors.cardBg,
                        padding: '32px',
                        borderRadius: '20px',
                        textAlign: 'center',
                        maxWidth: '90%',
                        width: '320px'
                    }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
                        <h2 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary, fontSize: '24px' }}>Puzzle Complete!</h2>
                        <p style={{ fontSize: '28px', color: theme.colors.accent, margin: '0 0 24px 0', fontWeight: 700 }}>
                            ⏱️ {formatTime(timer)}
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button onClick={resetPuzzle} style={{
                                padding: '14px 28px',
                                background: theme.colors.accent,
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: 700,
                                cursor: 'pointer'
                            }}>
                                🔄 Play Again
                            </button>
                            <button onClick={() => window.location.href = '/'} style={{
                                padding: '14px 28px',
                                background: '#e8e4de',
                                color: theme.colors.textPrimary,
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: 700,
                                cursor: 'pointer'
                            }}>
                                🏠 Home
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JigsawPlayer;
